// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectBuildDistributionDownloadMetricsByBucketId } from '@/selectors/selectors';
import { BuildDistroBuild } from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useChartColors } from '../styles/themeMode';
import {
    Bar,
    BarChart as RechartsBarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Skeleton } from 'antd/lib';
import Typography from 'antd/lib/typography';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

interface BucketDownloadMetricsContainerProps extends AppComponentProps {
    bucketId: string;
    isLoading?: boolean;
}

export const BucketDownloadMetricsContainer: React.FC<BucketDownloadMetricsContainerProps> = (
    props: BucketDownloadMetricsContainerProps,
) => {
    const chartColors = useChartColors();
    const { bucketId, isLoading } = props;

    const metrics = useSelector(
        (state: RootState) =>
            selectBuildDistributionDownloadMetricsByBucketId(state, bucketId)?.numDownloadsPerBuild || {},
    );
    const buildsById = useSelector(
        (state: RootState) => state.entities.buildDistribution.buildsById as Record<string, BuildDistroBuild>,
    );

    const { chartData } = React.useMemo(() => {
        const data = Object.entries(metrics).map(([buildId, value]) => {
            return {
                build: buildId,
                value,
            };
        });

        return {
            chartData: data,
        };
    }, [metrics, buildsById]);

    const formatBuildLabel = (buildId: string) => {
        return /^\d+$/.test(buildId) ? `#${buildId}` : buildId;
    };

    const rechartsTooltip = React.useCallback(
        ({
            active,
            payload,
            label,
        }: {
            active?: boolean;
            payload?: Array<{ value?: number }>;
            label?: string | number;
        }) => {
            if (!active || !payload?.length) {
                return null;
            }

            return (
                <div
                    style={{
                        backgroundColor: chartColors.tooltipBg,
                        color: chartColors.tooltipText,
                        borderRadius: 6,
                        padding: '8px 12px',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.16)',
                    }}
                >
                    <div
                        style={{
                            fontWeight: 600,
                            marginBottom: 4,
                        }}
                    >
                        {formatBuildLabel(String(label || ''))}
                    </div>
                    <div>Total downloads: {payload[0]?.value ?? 0}</div>
                </div>
            );
        },
        [chartColors, formatBuildLabel],
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                gridColumn: 'auto / span 3',
                alignItems: 'stretch',
                position: 'relative',
            }}
        >
            {isLoading ?
                <Skeleton
                    active
                    round={false}
                    style={{
                        padding: 12,
                    }}
                    paragraph={{
                        rows: 3,
                    }}
                />
            :   <>
                    <div
                        style={{
                            height: 56,
                        }}
                    >
                        <div
                            style={{
                                padding: '16px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                height: '100%',
                                position: 'relative',
                                gap: 4,
                            }}
                        >
                            <div
                                style={{
                                    height: 22,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 7,
                                    alignItems: 'center',
                                }}
                            >
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey8,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        maxWidth: 200,
                                        marginBottom: 0,
                                    }}
                                    ellipsis={{
                                        tooltip: 'Number of build downloads',
                                    }}
                                >
                                    Number of build downloads
                                </Typography.Text>
                                <TooltipInfoIcon tooltipContent="Number of times each build was downloaded in the selected time range." />
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: 200,
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsBarChart
                                data={chartData}
                                margin={{
                                    top: 20,
                                    right: 20,
                                    bottom: 6,
                                    left: 20,
                                }}
                            >
                                <CartesianGrid stroke={antGrey.grey4} vertical={false} />
                                <XAxis
                                    dataKey="build"
                                    tickFormatter={formatBuildLabel}
                                    tickLine={false}
                                    axisLine={false}
                                    stroke={antGrey.grey6}
                                    height={36}
                                />
                                <YAxis tickLine={false} axisLine={false} stroke={antGrey.grey6} allowDecimals={false} />
                                <RechartsTooltip
                                    content={rechartsTooltip}
                                    cursor={{ fillOpacity: 0.06 }}
                                    wrapperStyle={{ zIndex: 1000 }}
                                />
                                <Bar dataKey="value" fill={runwayColors.primary} radius={[4, 4, 0, 0]} />
                            </RechartsBarChart>
                        </ResponsiveContainer>
                    </div>
                </>
            }
        </div>
    );
};
